import { washService } from './firebase'
import config from '../config/environment'

const DEV_MODE = config.devMode

/** No hard queue cap — branches may take as many cars as needed. */
const UNLIMITED = null

export class CapacityService {
  /**
   * Queue size info for a branch. Always accepts new washes (no max limit).
   */
  static async canAcceptWash(branchId) {
    try {
      if (!branchId || branchId === 'all-branches') {
        return {
          canAccept: true,
          currentQueueSize: 0,
          maxCapacity: UNLIMITED,
          availableSlots: UNLIMITED,
          utilization: 0,
          unlimited: true,
        }
      }

      const currentQueue = await this.getCurrentQueue(branchId)
      const currentQueueSize = currentQueue.length

      return {
        canAccept: true,
        currentQueueSize,
        maxCapacity: UNLIMITED,
        availableSlots: UNLIMITED,
        utilization: 0,
        unlimited: true,
      }
    } catch (error) {
      console.error('Error checking capacity:', error)
      return {
        canAccept: true,
        currentQueueSize: 0,
        maxCapacity: UNLIMITED,
        availableSlots: UNLIMITED,
        utilization: 0,
        unlimited: true,
      }
    }
  }

  /**
   * Get current wash queue for branch
   */
  static async getCurrentQueue(branchId) {
    try {
      if (DEV_MODE) {
        return [
          { id: 'wash-1', status: 'waiting' },
          { id: 'wash-2', status: 'washing' },
        ]
      }

      const washes = await washService.getWashesByBranch(branchId)
      return washes.filter((wash) =>
        ['waiting', 'washing', 'drying', 'polishing', 'interior', 'ready'].includes(wash.status)
      )
    } catch (error) {
      console.error('Error getting current queue:', error)
      throw new Error('Failed to get current queue')
    }
  }

  /**
   * Validate wash creation — capacity never blocks.
   */
  static async validateWashCreation(branchId) {
    try {
      const capacityCheck = await this.canAcceptWash(branchId)

      return {
        isValid: true,
        errors: [],
        warnings: [],
        capacityInfo: capacityCheck,
      }
    } catch (error) {
      console.error('Error validating wash creation:', error)
      // Fail open so check-in is never blocked by capacity tooling
      return {
        isValid: true,
        errors: [],
        warnings: [],
        capacityInfo: {
          canAccept: true,
          unlimited: true,
          currentQueueSize: 0,
          maxCapacity: UNLIMITED,
        },
      }
    }
  }

  /**
   * Get estimated wait time for new customer
   */
  static async getEstimatedWaitTime(branchId) {
    try {
      const queue = await this.getCurrentQueue(branchId)
      const waitingWashes = queue.filter((wash) => wash.status === 'waiting')
      const averageWashTime = 45 // minutes
      const estimatedMinutes = waitingWashes.length * averageWashTime

      return {
        estimatedMinutes,
        queuePosition: waitingWashes.length + 1,
        message: `Estimated wait time: ${estimatedMinutes} minutes`,
      }
    } catch (error) {
      console.error('Error calculating wait time:', error)
      throw new Error('Failed to calculate wait time')
    }
  }
}

export const capacityService = CapacityService
